import {
  Checkbox,
  ImageSwatch,
  Label,
  SwatchPicker,
  Text,
  MessageBar,
} from "@fluentui/react-components";
import i18n, { use } from "i18next";
import React, { useState, useEffect } from "react";
import { baseUrl } from "../../helpers/constants";
import { fetchWrapper } from "../../helpers/fetchwrapper";
import AngelHeadSelectionColumns from "./AngelHeadColumns";
import { IWinkelkopf } from "../../Models/IWinkelkopf";
import useWindowSize from "../../helpers/useWindowSize";
import LuebArticleDetailsDialog from "../../Controls/LuebArticleDetailsDialog";
import LuebPagedDataGrid from "../../Controls/LuebPagedDataGrid";
import { ILuebOption } from "../../Models/ILuebOption";

interface IAngleHeadSelectionProps {
  configurationParameter: any;
  onSelectionChanged: any;
  onSelectedAntriebsset: any;
  preiseSichtbar: boolean;
}

function AngleHeadSelection(props: IAngleHeadSelectionProps) {
  const windowSize = useWindowSize();
  const imageSize = windowSize.width > 500 ? 230 : 140;

  const [selectedAngleHeadOptionKey, setSelectedAngleHeadOptionKey] =
    useState("90");

  const [artikelDetails, setArtikelDetails] = useState({
    artikelId: "",
    benennungDE: "",
    benennungEN: "",
  });

  const [winkelkoepfe, setWinkelkoepfe] = useState<IWinkelkopf[]>([]);
  const [loadingWinkelkoepfe, setLoadingWinkelkoepfe] = useState(false);
  const [selectedAngleHead, setSelectedAngleHead] = useState<
    IWinkelkopf | undefined
  >(undefined);
  const [selectedAntriebsset, setSelectedAntriebsset] = useState<any>();

  const options: ILuebOption[] = [
    {
      key: "90",
      icon: `${baseUrl}/IMAGES/WINKELKOPF/WINKELKOPF_90_250X250.PNG`,
      text: "L.SP3 angle - 90°",
    },
    {
      key: "110",
      icon: `${baseUrl}/IMAGES/WINKELKOPF/WINKELKOPF_110_250X250.PNG`,
      text: "L.SP3 angle - 110°",
    },
    {
      key: "nonLueb",
      icon: `${baseUrl}/IMAGES/WINKELKOPF/FREMDWINKELKOPF_250X250.PNG`,
      text: i18n.t<string>("nonLuebAngleHead"),
    },
  ];

  useEffect(() => {
    returnWinkelkopf();

    getAntriebsset();
  }, [selectedAngleHead, selectedAngleHeadOptionKey]);

  useEffect(() => {
    props.onSelectedAntriebsset(selectedAntriebsset);
  }, [selectedAntriebsset]);

  useEffect(() => {
    const achswinkel = selectedAngleHeadOptionKey;

    let kab2 = 0;
    let kab = 0;
    let artikelIdFl = "";

    if (selectedAngleHeadOptionKey == "") {
      return;
    }

    if (
      props.configurationParameter?.lsp3Selection?.items?.selectedLsp3Article
    ) {
      artikelIdFl =
        props.configurationParameter?.lsp3Selection?.items?.selectedLsp3Article
          .artikelId;
    }

    if (selectedAngleHeadOptionKey != "nonLueb") {
      // Wenn Indexing Adapter vorhanden, KAB = KAN vom Indexing Adapter. Sonst KAB2 = KAN vom Antriebsset
      kab2 = props.configurationParameter?.lsp3Connect?.selectedAntriebsset
        ?.kennzahlAntrieb
        ? props.configurationParameter?.lsp3Connect?.selectedAntriebsset
            ?.kennzahlAntrieb
        : 0;
      kab = props.configurationParameter?.lsp3Connect?.selectedAdapter
        ?.kennzahlAntrieb
        ? props.configurationParameter?.lsp3Connect?.selectedAdapter
            ?.kennzahlAntrieb
        : 0;
    }

    // Wenn nonLueb, wird WK nach Kundenspezifikation ausgewählt. Im Nachgang wird dann das Antriebsset ebenfalls ausgetauscht.
    if (achswinkel) {
      getWinkelkoepfe(kab, artikelIdFl, Number(achswinkel));
    }
  }, [selectedAngleHeadOptionKey]);

  const getWinkelkoepfe = (
    kab: number,
    artikelIdFl: string,
    achswinkel: number,
  ) => {
    setLoadingWinkelkoepfe(true);

    let type = "";
    switch (props.configurationParameter.gearbox.key) {
      case "Standard_FL_G":
        type = "lsp3_c_outline";
        break;
      case "Inline_FL_G":
        type = "lsp3_c_inline";
        break;
      case "Standard_FL_O":
        type = "lsp3_o_outline";
        break;
      case "Inline_FL_O":
        type = "lsp3_o_inline";
        break;
      case "Axial_Abtrieb":
        type = "lsp3_straight";
        break;
      case "Winkelkopf":
        type = "winkelkopf";
        break;
    }

    fetchWrapper
      .getWithAuth(
        `${baseUrl}/api/Configurator/GetWinkelkoepfe?kab=${kab}&artikelIdFl=${artikelIdFl}&type=${type}&achswinkel=${achswinkel}`,
      )
      .then((result: IWinkelkopf[]) => {
        setWinkelkoepfe(result);
        setLoadingWinkelkoepfe(false);
      })
      .catch((error) => {
        setLoadingWinkelkoepfe(false);
      });
  };

  const getAntriebsset = () => {
    let laengenkennziffer = "L0"; // Wenn Flange dann L2
    //const indexing = selectedZubehoerKey == "connectIndex" ? "ja" : ""; // Wenn Indexing dann "ja"

    if (
      props.configurationParameter?.lsp3Selection?.items
        ?.selectedZwischenelement?.laengenkennziffer
    ) {
      laengenkennziffer =
        props.configurationParameter?.lsp3Selection?.items
          ?.selectedZwischenelement?.laengenkennziffer;
    } else if (
      props.configurationParameter?.lsp3Connect?.selectedZubehoerKey ==
      "connectFlange"
    ) {
      laengenkennziffer = "L2";
    }

    const kan =
      props.configurationParameter.lsp3Selection?.items?.selectedLsp3Article
        ?.kennzahlAntrieb;

    const kab = selectedAngleHead?.kennzahlNuss;

    const type =
      props.configurationParameter?.gearbox?.key == "Standard_FL_G" ||
      props.configurationParameter?.gearbox?.key == "Inline_FL_G"
        ? "geschlossen"
        : "offen";

    /* console.log(props.configurationParameter.gearbox.key); */ // Standard_FL_G && Inline_FL_G == geschlossen!

    fetchWrapper
      .getWithAuth(
        `${baseUrl}/api/Configurator/GetAntriebsset?kennzahlAntrieb=${kan}&kennzahlAbtrieb=${kab}&laengenkennziffer=${laengenkennziffer}&type=${type}`, //&indexing=${indexing}
      )
      .then((result: any) => {
        if (result != "") {
          setSelectedAntriebsset(result);
        }
      });
  };

  useEffect(() => {
    if (winkelkoepfe.length == 1) {
      setSelectedAngleHead(winkelkoepfe[0]);
    }
  }, [winkelkoepfe]);

  const returnWinkelkopf = () => {
    props.onSelectionChanged({
      nonLuebAngleHead: selectedAngleHeadOptionKey == "nonLueb",
      withOutAngleHead: selectedAngleHeadOptionKey == "",
      selectedAngleHead: selectedAngleHead,
    });
  };

  const getSelectedAngleHead = () => {
    /* const selectedItem = selectionAngleHead.getSelection(); */

    if (selectedAngleHead == undefined) {
      setSelectedAngleHead(winkelkoepfe[0]);
    } else {
      setSelectedAngleHead(undefined);
    }
  };

  const columns = AngelHeadSelectionColumns({
    preiseSichtbar: props.preiseSichtbar,
    selectedWinkelKopf: selectedAngleHead,
  });
  return (
    <>
      <div className="lueb-MarginTop10">
        <LuebArticleDetailsDialog
          artikelId={artikelDetails?.artikelId}
          benennungDE={artikelDetails?.benennungDE}
          benennungEN={artikelDetails?.benennungEN}
          isDialogOpen={artikelDetails.artikelId != "" ? true : false}
          onClose={() => {
            setArtikelDetails({
              artikelId: "",
              benennungDE: "",
              benennungEN: "",
            });
          }}
        />

        <SwatchPicker
          selectedValue={selectedAngleHeadOptionKey}
          onSelectionChange={(ev, val) => {
            setSelectedAngleHead(undefined);
            setSelectedAngleHeadOptionKey(val.selectedValue);
          }}
        >
          <div style={{ display: "flex", flexWrap: "wrap" }}>
            {options.map((item) => {
              return (
                <div
                  style={{
                    display: "flex",
                    flexDirection: "column",
                    alignItems: "center",
                    justifyContent: "center",
                    margin: "2px",
                  }}
                >
                  <ImageSwatch
                    style={{
                      width: 230,
                      height: 230,
                    }}
                    value={typeof item.key == "string" ? item.key : ""}
                    src={item.icon ? item.icon : ""}
                    aria-label={item.text}
                  />
                  <Label style={{ marginTop: "5px" }} weight="semibold">
                    {item.text}
                  </Label>
                </div>
              );
            })}
          </div>
        </SwatchPicker>
        <Checkbox
          label={i18n.t<string>("withOutAngleHead")}
          checked={selectedAngleHeadOptionKey == ""}
          onChange={(ev, data) => {
            if (data.checked) {
              setSelectedAngleHeadOptionKey("");
              setSelectedAngleHead(undefined);
            }
          }}
        />

        {selectedAngleHeadOptionKey != "" && winkelkoepfe.length > 0 && (
          <div>
            <LuebPagedDataGrid
              selectionMode="single"
              onPageChanged={() => {
                setSelectedAngleHead(undefined);
              }}
              selectedItems={
                selectedAngleHead
                  ? new Set([winkelkoepfe.indexOf(selectedAngleHead)])
                  : new Set([])
              }
              onSelectionChange={(ev, data) => {
                const selItem = data.selectedItems;
                winkelkoepfe.forEach((obj, index) => {
                  if (selItem.has(index)) {
                    setSelectedAngleHead(obj);
                  }
                });
              }}
              columns={columns.columns}
              items={winkelkoepfe}
              resizableColumns={true}
              columnSizingOptions={{
                artikelId: { defaultWidth: 80, minWidth: 60 },
                checkedId: { defaultWidth: 50, minWidth: 50 },
                benennungM1: { defaultWidth: 100, minWidth: 100 },
                uebersetzungId: { defaultWidth: 160, minWidth: 100 },
              }}
            />
            {props.preiseSichtbar && (
              <Text style={{ marginTop: 20 }}>
                <Text style={{ color: "#ff6600", fontWeight: 700 }}>*</Text>
                {i18n.t<string>("priceNote")}
              </Text>
            )}
          </div>
        )}

        {selectedAngleHeadOptionKey != "nonLueb" &&
          selectedAngleHeadOptionKey != "" &&
          winkelkoepfe.length == 0 &&
          !loadingWinkelkoepfe && (
            <MessageBar intent="error">
              {i18n.t<string>("noSuitableAngleHeadFound")}
            </MessageBar>
          )}
      </div>
    </>
  );
}

export default AngleHeadSelection;
